/**
 * Shared UI utilities.
 */

import { openInTool, getOpenInTargets, getToolRelevance } from '../panel.js';
import { track } from './analytics.js';

function setOpenInState(container, open) {
  const trigger = container?.querySelector('.open-in__trigger');
  const menu = container?.querySelector('.open-in__menu');
  if (!trigger || !menu) return;
  menu.hidden = !open;
  trigger.setAttribute('aria-expanded', String(open));
}

function closeOpenInMenus(exceptContainer = null) {
  document.querySelectorAll('.open-in').forEach((container) => {
    if (container !== exceptContainer) setOpenInState(container, false);
  });
}

/**
 * A result's text for Copy, Clip, Open in, Pin and Try next: '' while the output shows a
 * prompt (a .placeholder-text). An output marked `data-exact` (steganography, where invisible
 * characters and the whitespace at its ends are the message) is taken as it is, never trimmed.
 */
export function resultText(output) {
  if (!output || output.querySelector('.placeholder-text')) return '';
  return output.hasAttribute('data-exact') ? output.textContent : (output.innerText || output.textContent).trim();
}

// Debounce — delays fn until wait ms after last call
export function debounce(fn, wait) {
  let timer;
  return (...args) => {
    clearTimeout(timer);
    timer = setTimeout(() => fn(...args), wait);
  };
}

// Copy text to clipboard with button feedback
export function copyToClipboard(text, btn) {
  navigator.clipboard
    .writeText(text)
    .then(() => {
      const orig = btn.textContent;
      btn.textContent = '\u2713';
      setTimeout(() => {
        btn.textContent = orig;
      }, 1500);
    })
    .catch(() => {});
}

/**
 * Creates an "Open in..." dropdown menu for cross-tool actions.
 * Uses smart relevance tiers: Suggested → Related → More tools.
 * @param {Function} getTextFn — called on click, returns the text to send
 * @param {string} currentToolId — excluded from the menu
 * @returns {HTMLElement}
 */
export function createOpenInMenu(getTextFn, currentToolId) {
  const container = document.createElement('div');
  container.className = 'open-in';

  const btn = document.createElement('button');
  btn.className = 'open-in__trigger';
  btn.textContent = 'Open in\u2026';
  btn.setAttribute('aria-label', 'Open in another tool');
  btn.setAttribute('aria-haspopup', 'menu');
  btn.setAttribute('aria-expanded', 'false');

  const menu = document.createElement('div');
  menu.className = 'open-in__menu';
  menu.hidden = true;

  const targets = getOpenInTargets(currentToolId);
  const relevance = getToolRelevance(currentToolId);
  const suggestedIds = new Set(relevance?.suggested || []);
  const relatedIds = new Set(relevance?.related || []);

  const suggested = [];
  const related = [];
  const rest = [];

  targets.forEach((t) => {
    if (suggestedIds.has(t.id)) suggested.push(t);
    else if (relatedIds.has(t.id)) related.push(t);
    else rest.push(t);
  });

  // Preserve order from the relevance arrays
  suggested.sort((a, b) => [...suggestedIds].indexOf(a.id) - [...suggestedIds].indexOf(b.id));
  related.sort((a, b) => [...relatedIds].indexOf(a.id) - [...relatedIds].indexOf(b.id));

  function makeItem(t) {
    const item = document.createElement('button');
    item.className = 'open-in__item';
    item.textContent = t.label;
    item.addEventListener('click', () => {
      const text = getTextFn();
      if (text) {
        track('open-in', { from: currentToolId || '', to: t.id });
        openInTool(t.id, { text });
      }
      setOpenInState(container, false);
    });
    return item;
  }

  function addGroup(label, tools) {
    if (!tools.length) return null;
    const group = document.createElement('div');
    group.className = 'open-in__group';
    const lbl = document.createElement('div');
    lbl.className = 'open-in__group-label';
    lbl.textContent = label;
    group.appendChild(lbl);
    tools.forEach((t) => group.appendChild(makeItem(t)));
    menu.appendChild(group);
    return group;
  }

  addGroup('Suggested', suggested);
  addGroup('Related', related);

  // "More tools" — collapsed by default, grouped by category
  if (rest.length) {
    const moreWrapper = document.createElement('div');
    moreWrapper.className = 'open-in__more';

    const toggle = document.createElement('button');
    toggle.className = 'open-in__more-toggle';
    toggle.textContent = `More tools (${rest.length})`;
    toggle.addEventListener('click', (e) => {
      e.stopPropagation();
      moreWrapper.classList.toggle('open-in__more--open');
    });
    moreWrapper.appendChild(toggle);

    const moreContent = document.createElement('div');
    moreContent.className = 'open-in__more-content';

    // Group rest by category
    const grouped = new Map();
    rest.forEach((t) => {
      if (!grouped.has(t.categoryLabel)) grouped.set(t.categoryLabel, []);
      grouped.get(t.categoryLabel).push(t);
    });

    grouped.forEach((tools, catLabel) => {
      const group = document.createElement('div');
      group.className = 'open-in__group';
      const lbl = document.createElement('div');
      lbl.className = 'open-in__group-label';
      lbl.textContent = catLabel;
      group.appendChild(lbl);
      tools.forEach((t) => group.appendChild(makeItem(t)));
      moreContent.appendChild(group);
    });

    moreWrapper.appendChild(moreContent);
    menu.appendChild(moreWrapper);
  }

  btn.addEventListener('click', (e) => {
    e.stopPropagation();
    const nextOpen = menu.hidden;
    closeOpenInMenus(nextOpen ? container : null);
    setOpenInState(container, nextOpen);
  });

  container.appendChild(btn);
  container.appendChild(menu);
  return container;
}

/**
 * A button that opens a short menu of actions, styled and closed like Open in…
 * @param {string} label — the button's text
 * @param {{ label: string, run: Function }[]} items
 * @returns {HTMLElement}
 */
export function createActionMenu(label, items) {
  const container = document.createElement('div');
  container.className = 'open-in';
  const btn = document.createElement('button');
  btn.type = 'button';
  btn.className = 'open-in__trigger';
  btn.textContent = label;
  btn.setAttribute('aria-haspopup', 'menu');
  btn.setAttribute('aria-expanded', 'false');
  const menu = document.createElement('div');
  menu.className = 'open-in__menu';
  menu.hidden = true;
  for (const item of items) {
    const el = document.createElement('button');
    el.type = 'button';
    el.className = 'open-in__item';
    el.textContent = item.label;
    el.addEventListener('click', () => {
      setOpenInState(container, false);
      item.run();
    });
    menu.appendChild(el);
  }
  btn.addEventListener('click', (e) => {
    e.stopPropagation();
    const nextOpen = menu.hidden;
    closeOpenInMenus(nextOpen ? container : null);
    setOpenInState(container, nextOpen);
  });
  container.append(btn, menu);
  return container;
}

function initOpenInDismissal() {
  document.addEventListener('click', (e) => {
    if (!e.target.closest('.open-in')) closeOpenInMenus();
  });
  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape') closeOpenInMenus();
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initOpenInDismissal);
} else {
  initOpenInDismissal();
}
